import React, { useState } from 'react'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import RightSlideOver from '../RightSlideOver'

const iconSizeClasses: Record<number, string> = {
  3: 'h-3 w-3',
  4: 'h-4 w-4',
  5: 'h-5 w-5',
  6: 'h-6 w-6',
}

export interface InlineHelpProps {
  title: string
  content: React.ReactNode
  size?: number
  /**
   * Render only the "?" trigger, without the inline `title` label. Use this when
   * the help sits next to an existing heading/label (e.g. a page <h1>) so the
   * title isn't duplicated. The button keeps its accessible name ("About {title}")
   * and the drawer still uses `title`.
   */
  hideLabel?: boolean
}

// A label that carries an inline "?" affordance. Clicking the icon opens a
// RightSlideOver drawer with `content`, keeping helper prose out of the page
// until it is asked for. Shared so every app uses one help pattern; the trigger
// is a real button so it is keyboard-reachable and has an accessible name.
const InlineHelp = ({ title, content, size, hideLabel }: InlineHelpProps) => {
  const [open, setOpen] = useState(false)

  return (
    <span>
      <RightSlideOver title={title} content={content} isOpen={open} setOpen={setOpen} iconOptional={QuestionMarkCircleIcon} />
      {!hideLabel && <>{title}&nbsp;</>}<button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true) }}
        aria-label={`About ${title}`}
        className="cursor-pointer align-middle inline-flex rounded-full text-perfect-shine-800 hover:text-perfect-shine-900 focus:outline-hidden focus:ring-2 focus:ring-perfect-shine"
      >
        <QuestionMarkCircleIcon className={iconSizeClasses[size || 4] || 'h-4 w-4'} aria-hidden="true" />
      </button>
    </span>
  )
}

export default InlineHelp
